import type { AnyError } from "@pdfking/errors"

import { Channel } from "./Channel"

/**
 * Creates a broadcast stream that forwards messages to all active subscribers.
 * Does not maintain memory of past messages - only new subscribers receive future messages.
 * Each subscriber gets their own independent async generator.
 *
 * @param initial - Optional array of initial messages to send to new subscribers
 * @returns Object with push, stop, and subscribe methods
 *
 * @example
 * ```ts
 * import { Broadcast } from "@pdfking/tfunc"
 *
 * const broadcast = Broadcast<string>()
 *
 * // Subscribe to messages
 * const sub1 = broadcast.subscribe()
 * const sub2 = broadcast.subscribe()
 *
 * // Push messages to all subscribers
 * broadcast.push("hello")
 * broadcast.push("world")
 *
 * // Stop the broadcast
 * broadcast.stop(undefined)
 *
 * // Consume messages
 * for await (const msg of sub1) {
 *   console.log(msg) // Outputs: "hello", "world"
 * }
 * ```
 */
export const Broadcast = <T>(
  initial?: T[]
): {
  push: (item: T) => void
  stop: (error: AnyError | undefined) => void
  subscribe: () => AsyncGenerator<T>
} => {
  const channels: ReturnType<typeof Channel<T>>[] = []

  return {
    push(item: T): void {
      channels.forEach(({ push }) => push(item))
    },

    stop(error: AnyError | undefined): void {
      channels.forEach(({ stop }) => stop(error))
    },

    subscribe(): AsyncGenerator<T> {
      const ch = Channel<T>(initial)
      channels.push(ch)
      return ch.stream
    },
  }
}
